import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type KeyboardEvent } from "react";
import { cn } from "../../lib/utils";

export interface AgentComposerAttachmentChip {
  id: string;
  label: string;
}

export interface AgentComposerUploadItem {
  id: string;
  label: string;
  progress: number;
  status: "uploading" | "processing";
}

interface AgentUIComposerProps {
  attachments?: AgentComposerAttachmentChip[];
  contextLabel?: string | null;
  disabled?: boolean;
  disabledReason?: string | null;
  initialValue?: string;
  isStreaming: boolean;
  isStopping?: boolean;
  isUploadingAttachments?: boolean;
  onAttachFiles?: (files: File[]) => void | Promise<void>;
  onRemoveAttachment?: (attachmentId: string) => void;
  onSend: (content: string) => void;
  onStop: () => void;
  placeholder?: string;
  uploadQueue?: AgentComposerUploadItem[];
}

export function AgentUIComposer({
  attachments = [],
  contextLabel,
  disabled,
  disabledReason,
  initialValue,
  isStreaming,
  isStopping = false,
  isUploadingAttachments = false,
  onAttachFiles,
  onRemoveAttachment,
  onSend,
  onStop,
  placeholder,
  uploadQueue = [],
}: AgentUIComposerProps) {
  const [value, setValue] = useState(initialValue ?? "");
  const [isDragActive, setIsDragActive] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue]);

  function resize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }

  function handleSend() {
    const trimmed = value.trim();
    if (!trimmed || disabled || isStreaming || isUploadingAttachments) return;
    onSend(trimmed);
    setValue("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }

  function handleAttachClick() {
    if (!onAttachFiles || disabled || isStreaming || isUploadingAttachments) return;
    fileInputRef.current?.click();
  }

  function handleDroppedFiles(files: File[]) {
    if (!onAttachFiles || disabled || isStreaming || isUploadingAttachments || files.length === 0) return;
    void onAttachFiles(files);
  }

  function isFileDrag(event: DragEvent<HTMLDivElement>) {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    if (!onAttachFiles) return;
    const files = event.target.files ? Array.from(event.target.files) : [];
    handleDroppedFiles(files);
    event.target.value = "";
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    if (!onAttachFiles || disabled || isStreaming || isUploadingAttachments) return;
    event.dataTransfer.dropEffect = "copy";
    setIsDragActive(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragActive(false);
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    setIsDragActive(false);
    if (!onAttachFiles || disabled || isStreaming || isUploadingAttachments) return;
    const files = Array.from(event.dataTransfer.files ?? []);
    handleDroppedFiles(files);
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 pb-6 pt-2">
      {contextLabel ? (
        <div className="mb-2 flex items-center gap-2 px-2">
          <span className="max-w-full truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
            {contextLabel}
          </span>
        </div>
      ) : null}
      {attachments.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 px-2">
          {attachments.map((attachment) => (
            <span
              key={attachment.id}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600"
            >
              <span className="truncate">{attachment.label}</span>
              {onRemoveAttachment ? (
                <button
                  type="button"
                  aria-label={`Remove ${attachment.label}`}
                  className="shrink-0 text-slate-400 transition hover:text-slate-700"
                  onClick={() => onRemoveAttachment(attachment.id)}
                >
                  ✕
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {uploadQueue.length > 0 ? (
        <div className="mb-3 rounded-[24px] border border-brand-100 bg-brand-50/70 px-4 py-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-brand-800">
              {uploadQueue.length === 1 ? "Uploading attachment" : `Uploading ${uploadQueue.length} attachments`}
            </p>
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-brand-700">
              {Math.round(uploadQueue.reduce((sum, item) => sum + item.progress, 0) / uploadQueue.length * 100)}%
            </span>
          </div>
          <div className="space-y-2">
            {uploadQueue.map((item) => (
              <div key={item.id}>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <span className="truncate text-sm text-slate-700">{item.label}</span>
                  <span className="text-xs text-slate-500">
                    {item.status === "processing" ? "Preparing preview…" : `${Math.max(1, Math.round(item.progress * 100))}%`}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full bg-brand-600 transition-[width] duration-150"
                    style={{ width: `${item.status === "processing" ? 100 : Math.max(4, Math.round(item.progress * 100))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div
        data-testid="agent-composer-dropzone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "grid grid-cols-[auto_minmax(0,1fr)_auto] items-end gap-3 rounded-[24px] border bg-white px-3 py-3 shadow-sm transition",
          isDragActive ? "border-brand-400 ring-2 ring-brand-100" : "border-slate-200",
        )}
      >
        {onAttachFiles ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              type="button"
              aria-label="Attach"
              onClick={handleAttachClick}
              disabled={disabled || isStreaming || isUploadingAttachments}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-lg text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploadingAttachments ? "…" : "+"}
            </button>
          </>
        ) : (
          <div className="h-10 w-10" aria-hidden />
        )}
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          disabled={disabled || isStreaming}
          onChange={(event) => {
            setValue(event.target.value);
            resize();
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? (isStreaming ? "HelloConvo is drafting the next step..." : "Ask for the next draft, revision, or recommendation")}
          className="max-h-[132px] min-h-[44px] w-full resize-none border-0 bg-transparent px-1 py-2 text-[15px] leading-7 text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-50"
        />
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            disabled={isStopping}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Stop"
          >
            ■
          </button>
        ) : (
            <button
              type="button"
              onClick={handleSend}
              disabled={!value.trim() || disabled || isUploadingAttachments}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-lg text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              aria-label="Send"
            >
            ↑
          </button>
        )}
      </div>
      {isDragActive ? (
        <p className="px-2 pt-2 text-xs text-brand-700">Drop PNG, JPEG, or WebP files to attach them to this turn.</p>
      ) : null}
      {(disabledReason || isUploadingAttachments) ? (
        <p className="px-2 pt-2 text-xs text-slate-400">
          {isUploadingAttachments ? "Uploading attachments…" : disabledReason}
        </p>
      ) : null}
    </div>
  );
}
